import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Facebook, Link2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import safeGtag from "../lib/ga";
import { trackEvent } from "../lib/fbpixel";
import { FadeUp } from "../components/Motion";
import PageMeta from "../components/PageMeta";

const stripMd = (s) => s
  .replace(/```[\s\S]*?```/g, " ")
  .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
  .replace(/^\s{0,3}#{1,6}\s+/gm, "")
  .replace(/^\s*[-*]\s+/gm, "")
  .replace(/\|/g, " ")
  .replace(/[*_`>]/g, "")
  .replace(/\s+/g, " ")
  .trim();

// Split article at its midpoint section so a CTA banner can sit between halves
const splitForBanner = (content) => {
  const sections = content.split(/\n(?=## )/);
  if (sections.length < 3) return [content, null];
  const mid = Math.ceil(sections.length / 2);
  return [sections.slice(0, mid).join("\n"), sections.slice(mid).join("\n")];
};

// Question-style "## …?" headings + their section text → FAQPage JSON-LD entries
const extractFaq = (content) => {
  const faqs = [];
  for (const sec of content.split(/\n(?=## )/)) {
    const m = sec.match(/^## +(.+\?)\s*\n([\s\S]*)/);
    if (m) {
      const a = stripMd(m[2]).slice(0, 800);
      if (a.length > 40) faqs.push({ q: m[1].trim(), a });
    }
  }
  return faqs.length >= 2 ? faqs : [];
};

const BANNER_HEADLINES = {
  "Landscaping in Edmonton": "Planning a landscaping project?",
  "Grading & Drainage Prep": "Need grading or drainage work?",
  "Sod & Lawn Care": "Dreaming of a lush new lawn?",
  "Paving & Patios": "Planning a patio, walkway or driveway?",
  "Retaining Walls & Drainage": "Planning a retaining wall?",
  "Artificial Grass": "Considering artificial grass?",
  "Plants & Trees": "Planning new trees or planting beds?",
  "Seasonal & Outdoor Living": "Getting your yard season-ready?",
};

// Blog category → recommended service card ("from" prices mirror ESTIMATOR_RATES)
const SERVICE_CARDS = {
  "Landscaping in Edmonton": { name: "Landscape Design & Build", to: "/residential/landscape-design", from: "Custom quote — free estimate" },
  "Grading & Drainage Prep": { name: "Final Grading (with Certificate)", to: "/estimate", from: "From $2,500" },
  "Sod & Lawn Care": { name: "Sod Installation", to: "/residential/sod-installation", from: "From $2 / sq ft" },
  "Paving & Patios": { name: "Paving Stones & Patios", to: "/residential/paving-stones", from: "From $40 / sq ft" },
  "Retaining Walls & Drainage": { name: "Retaining Walls", to: "/residential/retaining-walls", from: "From $45 / lin ft per layer" },
  "Artificial Grass": { name: "Artificial Turf Installation", to: "/estimate", from: "From $25 / sq ft" },
  "Plants & Trees": { name: "Tree Planting", to: "/residential/tree-planting", from: "From $600 / tree" },
  "Seasonal & Outdoor Living": { name: "Outdoor Living & Fire Features", to: "/residential/outdoor-living", from: "Custom quote — free estimate" },
};

const ServiceCard = ({ category }) => {
  const svc = SERVICE_CARDS[category];
  if (!svc) return null;
  return (
    <div data-testid="blog-service-card" className="mt-14 border border-[#D8D6D0]/20 bg-[#161B18] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div>
        <div className="overline text-[#8C1D30] mb-2">Recommended service</div>
        <div className="font-serif-display text-2xl md:text-3xl text-[#F5F3EF]" data-testid="blog-service-card-name">{svc.name}</div>
        <div className="mt-2 text-[#E2B85A] text-sm tracking-wide" data-testid="blog-service-card-price">{svc.from}</div>
      </div>
      <div className="flex flex-wrap gap-3 shrink-0">
        <Link
          data-testid="blog-service-card-estimate"
          to="/estimate"
          onClick={() => safeGtag("event", "blog_service_card_click", { destination: "estimate", category })}
          className="px-7 py-3.5 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors"
        >
          Get Exact Price
        </Link>
        {svc.to !== "/estimate" && (
          <Link
            data-testid="blog-service-card-link"
            to={svc.to}
            onClick={() => safeGtag("event", "blog_service_card_click", { destination: "service_page", category })}
            className="px-7 py-3.5 border border-[#D8D6D0]/30 text-[#F5F3EF] hover:border-[#D8D6D0]/70 text-xs uppercase tracking-widest transition-colors"
          >
            View Service
          </Link>
        )}
      </div>
    </div>
  );
};

const EstimateBanner = ({ category }) => (
  <div data-testid="blog-estimate-banner" className="my-14 bg-[#0B2B1F] border border-[#D8D6D0]/15 p-8 md:p-10">
    <div className="overline text-[#E2B85A] mb-3">Free &amp; no obligation</div>
    <div className="font-serif-display text-3xl md:text-4xl leading-tight text-[#F5F3EF]" data-testid="blog-banner-headline">
      {BANNER_HEADLINES[category] || "Planning a project like this?"}
    </div>
    <p className="text-[#D8D6D0]/70 mt-3">Get a realistic price range for your yard in under two minutes — from a local Edmonton team.</p>
    <div className="mt-6 flex flex-wrap gap-3">
      <Link
        data-testid="blog-banner-estimate"
        to="/estimate"
        onClick={() => safeGtag("event", "blog_banner_click", { destination: "estimate", category: category || "none" })}
        className="px-7 py-3.5 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest transition-colors"
      >
        Get Free Estimate
      </Link>
      <Link
        data-testid="blog-banner-designer"
        to="/yard-designer"
        onClick={() => safeGtag("event", "blog_banner_click", { destination: "yard_designer", category: category || "none" })}
        className="px-7 py-3.5 border border-[#D8D6D0]/30 text-[#F5F3EF] hover:border-[#D8D6D0]/70 text-xs uppercase tracking-widest transition-colors"
      >
        Try AI Yard Designer
      </Link>
    </div>
  </div>
);

const mdComponents = {
  h2: (props) => <h2 className="font-serif-display text-3xl md:text-4xl text-[#F5F3EF] mt-12 mb-4" {...props} />,
  h3: (props) => <h3 className="font-serif-display text-2xl md:text-3xl text-[#F5F3EF] mt-10 mb-3" {...props} />,
  p: (props) => <p className="text-[#D8D6D0]/70 text-lg leading-relaxed mt-5" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mt-5 space-y-2 text-[#D8D6D0]/70 text-lg" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mt-5 space-y-2 text-[#D8D6D0]/70 text-lg" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="text-[#F5F3EF] font-semibold" {...props} />,
  a: ({ href, children }) =>
    href?.startsWith("/") ? (
      <Link to={href} className="text-[#E2B85A] underline underline-offset-4 hover:text-[#F5F3EF] transition-colors">{children}</Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#E2B85A] underline underline-offset-4 hover:text-[#F5F3EF] transition-colors">{children}</a>
    ),
  table: (props) => <div className="overflow-x-auto mt-6"><table className="w-full text-left text-[#D8D6D0]/75 border border-[#D8D6D0]/15" {...props} /></div>,
  th: (props) => <th className="border border-[#D8D6D0]/15 px-4 py-2.5 text-[#F5F3EF] text-sm uppercase tracking-wider" {...props} />,
  td: (props) => <td className="border border-[#D8D6D0]/15 px-4 py-2.5" {...props} />,
  blockquote: (props) => <blockquote className="border-l-2 border-[#8C1D30] pl-6 mt-6 italic text-[#D8D6D0]/80" {...props} />,
};

export default function Blog() {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat");
  const setCat = (c) => setParams(c ? { cat: c } : {}, { replace: true });
  const [visibleCount, setVisibleCount] = useState(24);
  const [search, setSearch] = useState("");

  useEffect(() => { setVisibleCount(24); }, [cat, search]);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data));
  }, []);

  useEffect(() => {
    if (slug) {
      setPost(null);
      api.get(`/blog/${slug}`).then((r) => {
        setPost(r.data);
        trackEvent("ViewContent", {
          content_name: r.data.title,
          content_category: r.data.category || "Journal",
          content_ids: [r.data.slug],
          content_type: "article",
        });
      });
    }
  }, [slug]);

  const sharePage = (kind) => {
    const url = window.location.href;
    if (kind === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,width=600,height=500");
    } else {
      const fallbackCopy = () => {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        ok ? toast.success("Link copied to clipboard") : toast.error("Could not copy link");
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(() => toast.success("Link copied to clipboard"), fallbackCopy);
      } else {
        fallbackCopy();
      }
    }
  };

  if (slug && post) {
    const [contentBefore, contentAfter] = splitForBanner(post.content);
    const faqs = extractFaq(post.content);
    return (
      <div data-testid="blog-detail" className="pt-32 pb-32">
        <PageMeta
          title={`${post.title} — NorthCrest Journal`}
          description={post.excerpt || `${post.title} — landscaping journal from NorthCrest Edmonton.`}
          path={`/blog/${post.slug}`}
          image={post.cover_image}
          type="article"
        />
        <section className="px-6 md:px-12 lg:px-16 max-w-4xl mx-auto">
          <FadeUp>
            <nav data-testid="blog-breadcrumbs" aria-label="Breadcrumb" className="overline mb-6 flex flex-wrap items-center gap-2 text-[#D8D6D0]/50">
              <Link to="/blog" className="text-[#8C1D30] hover:text-[#F5F3EF] transition-colors">Journal</Link>
              {post.category && (
                <>
                  <span aria-hidden="true">→</span>
                  <Link to={`/blog?cat=${encodeURIComponent(post.category)}`} className="text-[#8C1D30] hover:text-[#F5F3EF] transition-colors">{post.category}</Link>
                </>
              )}
              <span aria-hidden="true">→</span>
              <span className="text-[#D8D6D0]/50">{post.title}</span>
            </nav>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Journal", item: "https://northcrestlandscaping.ca/blog" },
                    ...(post.category
                      ? [{ "@type": "ListItem", position: 2, name: post.category, item: `https://northcrestlandscaping.ca/blog?cat=${encodeURIComponent(post.category)}` }]
                      : []),
                    { "@type": "ListItem", position: post.category ? 3 : 2, name: post.title, item: `https://northcrestlandscaping.ca/blog/${post.slug}` },
                  ],
                }),
              }}
            />
            <h1 className="font-serif-display text-4xl md:text-6xl leading-none">{post.title}</h1>
            <div className="overline text-[#D8D6D0]/50 mt-6">{post.author} · {new Date(post.published_at).toLocaleDateString()}</div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="aspect-[16/8] overflow-hidden mt-12">
              <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="font-serif-display text-2xl leading-snug mt-12">{post.excerpt}</p>
            <div className="mt-8" data-testid="blog-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{contentBefore}</ReactMarkdown>
              <EstimateBanner category={post.category} />
              {contentAfter && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{contentAfter}</ReactMarkdown>
              )}
            </div>
            {faqs.length > 0 && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: faqs.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  }),
                }}
              />
            )}
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-14 pt-8 border-t border-[#D8D6D0]/15 flex items-center gap-4" data-testid="blog-share">
              <span className="overline text-[#D8D6D0]/50">Share</span>
              <button
                data-testid="blog-share-facebook"
                onClick={() => sharePage("facebook")}
                aria-label="Share on Facebook"
                className="w-11 h-11 flex items-center justify-center border border-[#D8D6D0]/25 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60 hover:text-[#F5F3EF] transition-colors"
              >
                <Facebook size={18} />
              </button>
              <button
                data-testid="blog-share-copy"
                onClick={() => sharePage("copy")}
                aria-label="Copy link"
                className="w-11 h-11 flex items-center justify-center border border-[#D8D6D0]/25 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60 hover:text-[#F5F3EF] transition-colors"
              >
                <Link2 size={18} />
              </button>
            </div>
            <ServiceCard category={post.category} />
          </FadeUp>
        </section>
        {(() => {
          const related = posts.filter((p) => p.category && p.category === post.category && p.slug !== post.slug).slice(0, 3);
          if (!related.length) return null;
          return (
            <section className="px-6 md:px-12 lg:px-16 max-w-7xl mx-auto mt-24" data-testid="blog-related">
              <FadeUp>
                <div className="overline text-[#8C1D30] mb-4">Keep reading</div>
                <h2 className="font-serif-display text-3xl md:text-4xl mb-10">Related articles</h2>
              </FadeUp>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {related.map((p) => (
                  <FadeUp key={p.id}>
                    <Link to={`/blog/${p.slug}`} data-testid={`blog-related-${p.slug}`} className="group block">
                      <div className="aspect-[4/3] overflow-hidden mb-5">
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                      </div>
                      <div className="overline text-[#D8D6D0]/40 mb-2">{p.category}</div>
                      <div className="font-serif-display text-xl md:text-2xl leading-tight">{p.title}</div>
                    </Link>
                  </FadeUp>
                ))}
              </div>
            </section>
          );
        })()}
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const filtered = posts.filter(
    (p) => (!cat || p.category === cat) && (!q || p.title.toLowerCase().includes(q) || (p.excerpt || "").toLowerCase().includes(q)),
  );

  return (
    <div data-testid="blog-list" className="pt-32 pb-32">
      <PageMeta
        title="Journal — Landscaping Insights from NorthCrest Edmonton"
        description="Notes from the studio. Landscaping design ideas, project breakdowns, and seasonal tips from NorthCrest Landscaping in Edmonton, Alberta."
        path="/blog"
      />
      <section className="px-6 md:px-12 lg:px-16 mb-16">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Journal</div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-none">
            Notes from <span className="italic text-[#D8D6D0]">the studio.</span>
          </h1>
        </FadeUp>
      </section>
      <section className="px-6 md:px-12 lg:px-16 mb-8">
        <div className="relative max-w-xl">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D8D6D0]/40 pointer-events-none" />
          <input
            data-testid="blog-search-input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles… (e.g. sod, retaining wall, grading)"
            className="w-full bg-transparent border border-[#D8D6D0]/25 pl-11 pr-4 py-3 text-sm text-[#F5F3EF] placeholder:text-[#D8D6D0]/35 focus:outline-none focus:border-[#D8D6D0]/60 transition-colors"
          />
        </div>
      </section>
      {(() => {
        const cats = [...new Set(posts.map((p) => p.category).filter(Boolean))];
        if (!cats.length) return null;
        return (
          <section className="px-6 md:px-12 lg:px-16 mb-12 flex flex-wrap gap-2" data-testid="blog-category-filter">
            <button
              data-testid="blog-cat-all"
              onClick={() => setCat(null)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${!cat ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]" : "border-[#D8D6D0]/25 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60"}`}
            >
              All
            </button>
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${cat === c ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]" : "border-[#D8D6D0]/25 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/60"}`}
              >
                {c}
              </button>
            ))}
          </section>
        );
      })()}
      <section className="px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filtered.slice(0, visibleCount).map((p, i) => (
          <FadeUp key={p.id} delay={Math.min(i, 8) * 0.05}>
            <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block">
              <div className="aspect-[4/3] overflow-hidden mb-6">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="overline text-[#D8D6D0]/40 mb-3">
                {new Date(p.published_at).toLocaleDateString()}
                {p.category && <span className="ml-3 text-[#8C1D30]">{p.category}</span>}
              </div>
              <div className="font-serif-display text-2xl md:text-3xl leading-tight">{p.title}</div>
              <p className="text-sm text-[#D8D6D0]/60 mt-4">{p.excerpt}</p>
            </Link>
          </FadeUp>
        ))}
      </section>
      {filtered.length === 0 && posts.length > 0 && (
        <div className="px-6 md:px-12 lg:px-16 text-[#D8D6D0]/50" data-testid="blog-no-results">
          No articles match your search. Try a different keyword.
        </div>
      )}
      {filtered.length > visibleCount && (
        <div className="px-6 md:px-12 lg:px-16 mt-16 flex justify-center">
          <button
            data-testid="blog-load-more"
            onClick={() => setVisibleCount((c) => c + 24)}
            className="px-8 py-4 text-xs uppercase tracking-widest border border-[#D8D6D0]/25 text-[#D8D6D0]/80 hover:border-[#D8D6D0]/60 hover:text-[#F5F3EF] transition-colors"
          >
            Load more articles
          </button>
        </div>
      )}
    </div>
  );
}
